<template>
  <div id="poster-batch-creation" class="pui-wrapper">
    <pui-common-spinner
      v-if="loader.messages.length"
      :message="loader.messages[0].message"
      class="global-loader"
      position="fixed"
    >
    </pui-common-spinner>

    <pui-error v-if="!loadingInitialData && errorLoadingInitialData" position="fixed">
      <i18n path="poster.batch_creation.error.primary"></i18n>
      <i18n path="poster.batch_creation.error.secondary">
        <template #btnReload>
          <span @click="reloadPage" class="pui-error__btn-reload">{{
            $t('poster.batch_creation.error.btnReload')
          }}</span>
        </template>
      </i18n>
    </pui-error>

    <template v-else-if="noRequiredDefaults">
      <!-- No Families -->
      <pui-error position="fixed" picto="mdi-alert">
        <i18n path="poster.batch_creation.no_families.primary"></i18n>
        <i18n path="poster.batch_creation.no_families.secondary"> </i18n>
      </pui-error>
    </template>

    <div
      v-else-if="!loadingInitialData && importConfiguration"
      class="batch-creation-wizard-scroll-wrapper"
    >
      <div :class="{ 'batch-creation-wizard-scroll-container': true, step2: showCart }">
        <!-- Step 1: search -->
        <pui-flex class="batch-creation-step-container step-1" direction="column">
          <pui-action-bar class="batch-creation-action-bar">
            <template #title>
              <i18n path="poster.batch_creation.title.step_1"></i18n>
            </template>

            <template #right>
              <pui-button
                @click="addToImportCart"
                :disabled="!selectedSearchResults.length"
                :outline="false"
                class="batch-creation-action-bar-btn"
                variant="primary"
              >
                {{ $t('poster.batch_creation.actions.step_1.add') }}
              </pui-button>
              <pui-button
                @click="
                  addToImportCart();
                  showCart = true;
                "
                :disabled="!selectedSearchResults.length"
                :outline="false"
                class="batch-creation-action-bar-btn"
                variant="primary"
                pictoRight="mdi-chevron-right"
              >
                {{ $t('poster.batch_creation.actions.step_1.add_and_finalize') }}
              </pui-button>
              <pui-button
                @click="showCart = true"
                :disabled="signagesToImport.length === 0"
                :outline="false"
                class="batch-creation-action-bar-btn"
                variant="primary"
                pictoRight="mdi-chevron-right"
              >
                {{ $t('poster.batch_creation.actions.step_1.finalize') }}
              </pui-button>
              <p class="pui-action-bar__action-subtitle">
                {{
                  $tc('poster.batch_creation.subtitle.step_1', signagesToImport.length, {
                    count: signagesToImport.length,
                  })
                }}
              </p>
            </template>
          </pui-action-bar>

          <poster-search-panel
            ref="importSearchPanel"
            :searchFunction="importSearch"
            :types="
              defaultTypeData != null && defaultTypeData.valuesTypes
                ? defaultTypeData.valuesTypes
                : null
            "
            :templateId="defaultTemplate != null ? defaultTemplate.templateId : null"
            :searchConfiguration="importConfiguration"
            :isMultiStepSearch="true"
            :showResetSearchForm="true"
            :searchModeFormStateProvider="restoreSearchModeFormState"
            :defaultSearchModeId="defaultSearchModeId"
            @changeSearchMode="changeSearchMode"
            @selectedSearchedItemsChanged="onSelectedSearchedItemsChanged"
            @clearSearchValues="clearSearchValues"
            class="search-panel"
          >
            <template #searchActions>
              <!-- Override slot but don't render anything -->
              <div v-show="false"></div>
            </template>
          </poster-search-panel>
        </pui-flex>

        <!-- Step 2: summary -->
        <pui-flex
          class="batch-creation-step-container step-1 batch-creation-search-summary"
          direction="column"
        >
          <pui-action-bar class="batch-creation-action-bar">
            <template #left>
              <pui-button
                @click="showCart = false"
                :outline="true"
                class="batch-creation-action-bar-btn"
                picto="mdi-chevron-left"
              >
                {{ $t('poster.batch_creation.actions.step_2.return') }}
              </pui-button>
            </template>

            <template #title>
              <i18n path="poster.batch_creation.title.step_2"></i18n>
            </template>

            <template #right>
              <pui-button
                v-if="showCart"
                :disabled="disableCreation"
                @click="importSignages"
                :outline="false"
                class="batch-creation-action-bar-btn"
                variant="primary"
              >
                {{ $t('poster.batch_creation.subtitle.step_2') }}
              </pui-button>
            </template>
          </pui-action-bar>

          <pui-flex class="search-summary-panel" direction="column">
            <div class="summary-header">
              <h3>
                {{ importConfiguration.summary.title | piivoTranslateLabel }}
              </h3>
              <p class="summary-informations">
                {{ $t('poster.batch_creation.formats.add_hint') }}
              </p>
              <div class="summary-actionbar">
                <pui-flex class="summary-buttons">
                  <pui-button
                    id="quick-import-modal-choose-model"
                    ref="chooseModelReference"
                    :disabled="!importValuesSelection.length"
                    variant="primary"
                    class="summary-button summary-choose-model-btn"
                  >
                    {{ $t('poster.batch_creation.type.find_type') }}
                  </pui-button>

                  <div v-if="!typeDataError && noFormatsForSelectedImportPosters">
                    <div class="summary-selector import-format-selector__error">
                      <span>{{ $t('poster.batch_creation.format.placeholder_no_formats') }}</span>
                    </div>
                  </div>
                  <pui-popper
                    ref="importFormatsCopiesPopper"
                    v-else
                    :disabled="!commonSelectedImportPosterFormats.length && !typeDataError"
                    @open="onFormatsCopiesPopperOpened"
                    @close="isFormatsCopiesFocused = false"
                    referenceClass="summary-format-groups-popper__reference"
                    class="summary-button summary-format-groups-popper"
                    tagName="div"
                    referenceTagName="div"
                    constrainWidth
                  >
                    <template #default="{ closePopper }">
                      <div
                        @focus="isFormatsCopiesFocused = true"
                        @keydown.enter.esc="closePopper"
                        class="summary-format-groups-popper-content"
                        tabindex="-1"
                      >
                        <pui-flex
                          v-if="typeDataError"
                          class="type-data-error-message"
                          alignItems="center"
                        >
                          <i class="mdi mdi-alert error-icon" />
                          <pui-flex direction="column">
                            <i18n path="poster.creation.error.type_data.message.primary"></i18n>
                            <i18n path="poster.creation.error.type_data.message.secondary">
                              <template #clickToReload>
                                <span @click="reloadPage" class="reload-button">{{
                                  $t('poster.creation.error.type_data.message.click_to_reload')
                                }}</span>
                              </template>
                            </i18n>
                          </pui-flex>
                        </pui-flex>
                        <pui-input-number-picker
                          ref="copiesPopupEditor"
                          v-else
                          v-for="(formatCopies, formatId) in importFormatCopiesMap"
                          :id="`quick-import-modal-input-copies-${formatId}`"
                          :key="formatId"
                          v-model="formatCopies.copies"
                          :min="MIN_COPIES"
                          :max="MAX_COPIES"
                          :step="1"
                          :label="piivoTranslate(formatCopies)"
                          @focus="isFormatsCopiesFocused = true"
                          @input="updateSignagesToImportCopies(formatId)"
                        />
                      </div>
                    </template>
                    <template #reference>
                      <pui-button
                        id="quick-import-modal-add-formats"
                        :disabled="!commonSelectedImportPosterFormats.length && !typeDataError"
                        variant="secondary"
                        outline
                        class="summary-format-groups-btn"
                      >
                        <template #contentBare>
                          <span class="label">{{ $t('poster.batch_creation.formats.add') }}</span>

                          <div class="arrow-zone ">
                            <span class="arrow-zone--arrow " />
                          </div>
                        </template>
                      </pui-button>
                    </template>
                  </pui-popper>

                  <pui-button
                    id="quick-import-modal-selection-delete"
                    :disabled="!importValuesSelection.length"
                    @click="deleteFromImportSelection"
                    outline
                    variant="secondary"
                    class="summary-delete-btn"
                  >
                    {{ $t('poster.batch_creation.formats.remove') }}
                  </pui-button>
                </pui-flex>
                <p
                  v-if="signagesToImportValues && signagesToImportValues.length > 0"
                  class="element-count"
                >
                  {{
                    $t('poster.batch_creation.formats.element_selected_count', {
                      count: signagesToImportValues.length,
                      selectedCount: importValuesSelection.length,
                    })
                  }}
                </p>
              </div>
              <pui-flex
                v-if="areImportPostersMissingTypes"
                class="missing-types"
                alignItems="center"
              >
                <i class="mdi mdi-alert-circle error-icon"></i>
                <span class="error-message">{{ $t('poster.batch_creation.missing_types') }}</span>
              </pui-flex>
            </div>
            <pui-table
              ref="importCartTable"
              :columns="importConfiguration.summary.columns"
              :allowSelection="true"
              :items="signagesToImportValues"
              :isCellSelectable="false"
              :columnSorts="currentSorts"
              @cellItemValueChanged="onSummaryCellValueChanged"
              @selectionChanged="importValuesSelection = $event"
              @columnSort="onColumnSort"
              :tableEditors="PosterTableEditors"
              :tableRenderers="PosterTableRenderers"
              :enableValidation="false"
              :getItemId="getSearchResultId"
            />
          </pui-flex>

          <pui-popper
            v-if="chooseModelReference"
            :options="{
              placement: 'bottom-start',
            }"
            :customReference="chooseModelReference"
            :disabled="!importValuesSelection.length"
            popperClass="summary-choose-model-popper__popper"
            class="summary-choose-model-popper__root"
          >
            <template #default="{ closePopper }">
              <poster-type-gallery
                ref="chooseModelGallery"
                :scopeFilter="PosterScopes.BatchCreation"
                :familyCarouselProps="{
                  defaultColCount: 5,
                  initialContainerWidth: 700,
                }"
                :typeThumbnailListeners="
                  (type, family) => ({
                    click: () => {
                      closePopper();
                      importTypeChanged(type, family);
                    },
                  })
                "
                :typeThumbnailProps="typeThumbnailProps"
                class="summary-choose-model-gallery"
              >
                <template #typeCenter="{ item: type, family }">
                  <pui-thumbnail-main-action
                    @click.stop="
                      closePopper();
                      importTypeChanged(type, family);
                    "
                  >
                    {{ $t('poster.batch_creation.type.select_type') }}
                  </pui-thumbnail-main-action>
                </template>
              </poster-type-gallery>
            </template>
          </pui-popper>
        </pui-flex>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';

import { getSortingManager } from '../../../core/extensionPoints/sortingManager';
import helpers from '../../../core/helpers';
import services from '../../../core/services';
import store from '../../../core/store';
import { Loader } from '../../../utils/loader';
import { PosterTableEditors } from '../components/table/editors';
import { PosterTableRenderers } from '../components/table/renderers';
import { MAX_COPIES, MIN_COPIES, PosterScopes } from '../constants';
import { POSTER_SESSION_SETTINGS, SessionSettings } from '../constants/sessionSettings';
import ConstantsMixin from '../mixins/ConstantsMixin';
import { getPosterService } from '../services';
import {
  NAMESPACE as POSTER_NAMESPACE,
  SET_BATCH_CREATION_DEFAULT_SEARCH_MODE_ID,
  SET_BATCH_CREATION_SEARCH_MODE_FORM_VALUES,
  SET_CURRENT_SIGNAGE_ITEM_ID,
  SET_LAST_BUILT_FAMILY,
  SET_LAST_BUILT_TYPE,
} from '../store/modules/poster';

export default {
  mixins: [ConstantsMixin],
  data() {
    return {
      importConfiguration: null,
      selectedSearchMode: null,
      selectedSearchResults: [],
      signagesToImport: [],
      importValuesSelection: [],
      importType: null,
      importFormatCopiesMap: {},
      isFormatsCopiesFocused: false,
      timeoutToCloseFormatsCopies: null,
      showCart: false,

      familiesTypes: { families: [], rootTypes: new Map() },
      importFamily: null,
      defaultTypeData: null,
      loader: new Loader(),
      loadingInitialData: true,
      errorLoadingInitialData: false,

      fontsError: false,
      typeDataError: false,

      chooseModelReference: null,
      PosterScopes,

      PosterTableEditors,
      PosterTableRenderers,

      MIN_COPIES,
      MAX_COPIES,
    };
  },
  watch: {
    showCart(nextValue) {
      if (nextValue && !this.chooseModelReference) {
        this.$nextTick(() => {
          this.initChooseModelReference();
        });
      }
    },
  },
  computed: {
    ...mapGetters({
      user: 'user',
      defaultSearchModeId: `${POSTER_NAMESPACE}/batchCreationDefaultSearchModeId`,
    }),
    /**
     * Gets or sets the poster cart in the store
     */
    posterCart: {
      get() {
        return getPosterService('cartState').get();
      },
      set(nextSignageItems) {
        getPosterService('cartState').set(nextSignageItems);
      },
    },
    /**
     * @returns {boolean} if there isn't any default family or type
     */
    noRequiredDefaults() {
      return (
        !this.loadingInitialData &&
        (!this.familiesTypes.families.length ||
          !this.familiesTypes.rootTypes.get(this.familiesTypes.families[0].itemId) ||
          !this.familiesTypes.rootTypes.get(this.familiesTypes.families[0].itemId).length ||
          !this.importConfiguration)
      );
    },
    importRootTypes() {
      if (!this.importFamily || !this.importFamily.itemId) {
        return [];
      }

      return this.familiesTypes.rootTypes.get(this.importFamily.itemId) || [];
    },
    /**
     * Get default template
     */
    defaultTemplate() {
      if (!this.defaultTypeData) {
        return null;
      }
      const defaultFormat = getPosterService('templates').getDefaultTemplateTypeFormat(
        this.defaultTypeData.formatGroups
      );
      return defaultFormat != null
        ? this.defaultTypeData.templates.get(defaultFormat.itemId)
        : null;
    },
    /**
     * @returns {boolean} if there are no formats associated to the selected import posters.
     * NOT the same thing as there being no *common* formats
     */
    noFormatsForSelectedImportPosters() {
      if (!this.signagesToImport.length || !this.importValuesSelection.length) {
        return false;
      }

      const uniqueTypeIds = [
        ...new Set(this.importValuesSelection.map(poster => poster.type.itemId)),
      ];

      for (const typeId of uniqueTypeIds) {
        const typeData = getPosterService('templates').getTypeData(
          typeId,
          PosterScopes.BatchCreation
        );
        if (!typeData) {
          return false;
        }
        const allTypeFormats = typeData.formatGroups.flatMap(formatGroup => formatGroup.formats);
        if (allTypeFormats.length) {
          return false;
        }
      }

      return true;
    },
    /**
     * @returns {array} the common formats of the selected import posters
     */
    commonSelectedImportPosterFormats() {
      if (!this.signagesToImport.length) {
        return [];
      }

      let commonFormats = [];
      const uniqueTypeIds = [
        ...new Set(this.importValuesSelection.map(poster => poster.type.itemId)),
      ];

      uniqueTypeIds.forEach((typeId, typeIdx) => {
        // Assume that we already loaded the type data
        const typeData = getPosterService('templates').getTypeData(
          typeId,
          PosterScopes.BatchCreation
        );
        if (!typeData) {
          return;
        }

        const allTypeFormats = typeData.formatGroups.flatMap(formatGroup => formatGroup.formats);
        const uniqueFormatIds = [...new Set(allTypeFormats.map(format => format.itemId))];
        const uniqueTypeFormats = uniqueFormatIds.map(itemId =>
          allTypeFormats.find(type => type.itemId === itemId)
        );

        if (typeIdx === 0) {
          // For the first group of formats of the first poster,
          // add them to the common array
          commonFormats.push(...uniqueTypeFormats);
        } else {
          // Otherwise, filter the existing array against
          // the current formats to get the common formats
          // bewteen the previous iterations and the current formats
          commonFormats = commonFormats.filter(
            cFormat => !!uniqueTypeFormats.find(f => cFormat.itemId === f.itemId)
          );
        }
      });

      return commonFormats;
    },
    /**
     * @returns {object[]} Compute signage item to import into values object for import selection.
     */
    signagesToImportValues() {
      return this.signagesToImport.map(signage => signage.values);
    },
    /**
     * @returns {boolean} if some import posters don't have any type
     */
    areImportPostersMissingTypes() {
      return this.signagesToImport.some(signage => !signage.values.type);
    },
    /**
     * @returns {boolean} if the creation button should be disabled
     */
    disableCreation() {
      return !this.signagesToImport.length || this.areImportPostersMissingTypes;
    },
    /**
     * @returns {object[]} the import types of the import posters
     */
    selectedPosterImportTypes() {
      const flatTypes = this.importRootTypes.map(type => [type, ...(type.children || [])]).flat();
      return flatTypes.filter(type =>
        this.importValuesSelection.some(poster => poster.type.itemId === type.itemId)
      );
    },
    /**
     * @returns {boolean} if all the selected posters have the same type
     */
    selectedPostersHaveSameType() {
      if (this.selectedPosterImportTypes.length <= 1) {
        return true;
      }

      const firstTypeId = this.selectedPosterImportTypes[0].itemId;
      for (const type in this.selectedPosterImportTypes.slice(1)) {
        if (firstTypeId !== type.itemId) {
          return false;
        }
      }

      return true;
    },
    /**
     * Gets or sets the current import cart sorts
     */
    currentSorts: {
      get() {
        return (
          services
            .getService('sessionSettings')
            .getInCategory(POSTER_SESSION_SETTINGS, SessionSettings.BATCH_CREATION_CART_SORTS) || []
        );
      },
      set(sorts) {
        services
          .getService('sessionSettings')
          .setInCategory(POSTER_SESSION_SETTINGS, SessionSettings.BATCH_CREATION_CART_SORTS, sorts);
      },
    },
  },
  methods: {
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    /**
     * @param {object} item - search result item
     * @returns {number} unique id for the item
     */
    getSearchResultId(item) {
      return item._id;
    },
    /**
     * Initializes the choose model popper reference
     */
    initChooseModelReference() {
      if (!this.chooseModelReference && this.$refs.chooseModelReference) {
        this.chooseModelReference = this.$refs.chooseModelReference.$el;
      }
    },
    /**
     * Callback function to apply props to a type thumbnail
     *
     * @param {object} type - the type in the thumbnail
     * @param {objet} family - the family of the type
     */
    typeThumbnailProps(type, family) {
      const checked = !!this.selectedPosterImportTypes.find(
        selectedType => selectedType.itemId === type.itemId
      );

      return {
        clickable: true,
        checked,
        indeterminate: checked && !this.selectedPostersHaveSameType,
      };
    },
    /**
     * Loads necessary data for the import
     */
    async loadInitialData() {
      this.loadingInitialData = true;
      const stopGlobalLoader = this.loader.addMessage();
      this.errorLoadingInitialData = false;

      try {
        this.familiesTypes = await getPosterService('templates').loadFamiliesAndTypes(
          PosterScopes.BatchCreation
        );
        if (!this.familiesTypes.families.length) {
          this.loadingInitialData = false;
          stopGlobalLoader();
          return;
        }

        this.importFamily = this.familiesTypes.families[0];
        const rootTypes = this.familiesTypes.rootTypes.get(this.importFamily.itemId) || [];
        if (!rootTypes.length) {
          this.loadingInitialData = false;
          stopGlobalLoader();
          return;
        }
        const defaultType = getPosterService('templates').getDefaultTemplateType(rootTypes);

        // Set the initial import type to be the default type
        this.importType = defaultType;

        const typeData = await this.getTypeData(defaultType);

        this.defaultTypeData = typeData;

        const stopImportConfigurationLoader = this.loader.addMessage(
          this.$t('poster.import.modes.loading')
        );
        try {
          this.importConfiguration = await getPosterService(
            'importModes'
          ).getImportConfigurationByTypeId(this.defaultTypeData.type.itemId);

          if (this.importConfiguration != null && this.importConfiguration.summary != null) {
            // Compute import summary columns
            helpers
              .getHelper('tableHelper')
              .computeTableColumns(
                this.importConfiguration.summary.columns,
                this.defaultTypeData != null ? this.defaultTypeData.attributeMap : null
              );
          }
        } finally {
          stopImportConfigurationLoader();
        }
      } catch (err) {
        this.errorLoadingInitialData = true;
        services.getService('alerts').alertError(this.$t('poster.creation.error.load_page_data'));
      }

      this.loadingInitialData = false;
      stopGlobalLoader();
    },
    /**
     * Get type data
     *
     * @param {object} type - type object
     * @returns Promise to get type data
     */
    async getTypeData(type) {
      if (!type) {
        return null;
      }

      const stopLoading = this.loader.addMessage();
      try {
        const existingTypeData = getPosterService('templates').getTypeData(
          type.itemId,
          PosterScopes.BatchCreation
        );
        if (existingTypeData) {
          stopLoading();
          return existingTypeData;
        } else {
          const typeData = await getPosterService('templates').loadTypeData(
            type,
            PosterScopes.BatchCreation
          );
          stopLoading();
          return typeData;
        }
      } catch (err) {
        this.fontsError = this.fontsError || err.fontsError;
        this.typeDataError = true;

        // Error
        services.getService('alerts').alertError(this.$t('poster.creation.error.type_data.alert'));
        stopLoading();
        throw err;
      }
    },
    /**
     * Triggered when the import copies popper is opened.
     * We recompute the common number of copies for the selected signages
     */
    onFormatsCopiesPopperOpened() {
      this.isFormatsCopiesFocused = true;

      this.importFormatCopiesMap = {};

      // Init formats copies numbers with common values (or '*' if no common value)
      for (const format of this.commonSelectedImportPosterFormats) {
        let commonValue = null;
        for (const signageValues of this.importValuesSelection) {
          if (commonValue === null) {
            commonValue = signageValues.formatsCopiesMap[format.itemId].copies;
          } else if (commonValue !== signageValues.formatsCopiesMap[format.itemId].copies) {
            commonValue = '*';
            break;
          }
        }
        this.importFormatCopiesMap[format.itemId] = {
          label: format.label,
          copies: commonValue || 0,
        };
      }

      // Set focus on first input
      this.$nextTick(() => {
        if (this.$refs.copiesPopupEditor && this.$refs.copiesPopupEditor[0]) {
          this.$refs.copiesPopupEditor[0].focus();
        }
      });
    },
    /**
     * Triggeted on blur of formats copies popup or copies inputs
     */
    blurFormatsCopiesPopup() {
      // Mark the popper as blurred
      this.isFormatsCopiesFocused = false;

      if (this.timeoutToCloseFormatsCopies) {
        clearTimeout(this.timeoutToCloseFormatsCopies);
      }

      // Delay closing the popper. Otherwise a click on the reference will
      // first trigger the blur, closing the popper. Then the click
      // will reopen the popper
      this.timeoutToCloseFormatsCopies = setTimeout(() => {
        if (!this.$refs.importFormatsCopiesPopper) {
          return;
        }

        // If nothing focused the popper, close it
        if (!this.isFormatsCopiesFocused) {
          this.$refs.importFormatsCopiesPopper.close();
          this.isFormatsCopiesFocused = false;
        }
      }, 10);
    },
    /**
     * For a given format, sync the selected import posters' copies
     * with the format copies popup's value
     * @param {String} formatId - The format identifier
     */
    updateSignagesToImportCopies(formatId) {
      for (const signageValues of this.importValuesSelection) {
        const copies = Number(this.importFormatCopiesMap[formatId].copies);
        if (Number.isInteger(copies) && copies >= 0) {
          signageValues.formatsCopiesMap[formatId].copies = copies;
        }
      }
    },
    /**
     * Launch on cell change for a signage item in the summary table.
     * @param {Object} item - signage item modified
     * @param {String} property - property name
     * @param {Object} value - new value
     */
    onSummaryCellValueChanged(item, property, value) {
      item[property] = value;
    },
    /**
     * Column sort event.
     * @param {[sortKey: string, sortDirection: string][]} sorts - sorts entries
     */
    onColumnSort(sorts) {
      this.currentSorts = sorts;
      this.sortCart();
    },
    /**
     * Sorts the cart
     */
    sortCart() {
      if (!this.currentSorts.length) {
        return;
      }
      this.signagesToImport.sort((a, b) => {
        let res = 0;
        for (const sort of this.currentSorts) {
          const sortColumnAlias = sort[0];
          const columnToSort = this.importConfiguration.summary.columns.find(
            col => col.alias === sortColumnAlias
          );
          if (!columnToSort) {
            return 0;
          }
          const sortFn = getSortingManager().getSortFn(columnToSort.sorter || 'alpha');
          if (!sortFn) {
            return 0;
          }

          res = sortFn(a.values[sortColumnAlias], b.values[sortColumnAlias], sort[1]);
          if (res !== 0) {
            break;
          }
        }

        return res;
      });
    },
    /**
     * Handles a change of the import type
     *
     * @param {object} type - the new type
     * @param {object} family - the family of the new type
     */
    importTypeChanged(type, family) {
      this.importFamily = family;
      this.importType = type;

      this.updateImportSelectionType(this.importType);
    },
    /**
     * Updates the type of the selected import posters
     *
     * @param {object} type - the poster type
     */
    async updateImportSelectionType(type) {
      const typeData = await this.getTypeData(type);

      this.importValuesSelection.forEach(poster => {
        if (poster.type.itemId === type.itemId) {
          return;
        }

        poster.type = type;

        // Initialize formats copies (preserves common formats)
        getPosterService('signages').initFormatsCopies(poster, typeData.formatGroups);
      });
    },
    /**
     * Change selected import mode.
     * @param {Object} importMode - New selected import mode
     */
    changeSearchMode(searchMode) {
      this.selectedSearchMode = searchMode;
      store.commit(
        `${POSTER_NAMESPACE}/${SET_BATCH_CREATION_DEFAULT_SEARCH_MODE_ID}`,
        this.selectedSearchMode.itemId
      );
      this.showCart = false;
    },
    /**
     * Called when the search results selection is changed
     *
     * @param {object[]} selectedSearchResults - the selected results
     */
    onSelectedSearchedItemsChanged(selectedSearchResults) {
      this.selectedSearchResults = selectedSearchResults;
    },
    /**
     * Clears the search values for the current search mode
     */
    clearSearchValues() {
      store.commit(`${POSTER_NAMESPACE}/${SET_BATCH_CREATION_SEARCH_MODE_FORM_VALUES}`, {
        itemId: this.selectedSearchMode.itemId,
        values: null,
      });
    },
    /**
     * @param {string} searchModeId - the id of the search mode for which to restore values
     * @returns {object} the values used for the search mode's last execution
     */
    restoreSearchModeFormState(searchModeId) {
      return store.getters[`${POSTER_NAMESPACE}/batchCreationSearchModeFormState`](searchModeId);
    },
    /**
     * Import search method.
     * @param {Object} searchValues - Object with current search values (key: alias, value: search value)
     * @param {object} pagination - object with pagination parameters
     * @param {object} pagination.index - page index to request
     * @param {object} pagination.size - page size
     * @param {[sortKey: string, sortDirection: string][]} sorts - sorts entries
     */
    async importSearch(searchValues, pagination, sorts) {
      if (
        !this.defaultTemplate ||
        !this.defaultTemplate.templateId ||
        !this.selectedSearchMode ||
        !this.selectedSearchMode.search ||
        !this.selectedSearchMode.search.searchSourceId ||
        !this.defaultTypeData
      ) {
        return [];
      }

      if (this.selectedSearchMode.search.preserveFormValues) {
        store.commit(`${POSTER_NAMESPACE}/${SET_BATCH_CREATION_SEARCH_MODE_FORM_VALUES}`, {
          itemId: this.selectedSearchMode.itemId,
          values: JSON.parse(JSON.stringify(searchValues)),
        });
      }

      // Prepare default values
      const defaultValues =
        this.user.extendedProperties != null
          ? JSON.parse(JSON.stringify(this.user.extendedProperties))
          : {};
      for (const alias in this.defaultTypeData.defaultValues) {
        if (
          this.defaultTypeData.defaultValues.hasOwnProperty(alias) &&
          typeof defaultValues[alias] === 'undefined'
        ) {
          defaultValues[alias] = this.defaultTypeData.defaultValues[alias];
        }
      }

      // Search with the values of:
      const parameters = [
        // The inputGroups of the poster form (with default values)
        {
          attrGroups:
            this.defaultTypeData.form != null ? this.defaultTypeData.form.inputGroups : null,
          values: defaultValues,
        },
        // The attributeGroups of the search form
        {
          attrGroups: this.selectedSearchMode.search.searchForm
            ? this.selectedSearchMode.search.searchForm.attributeGroups
            : [],
          values: searchValues,
        },
      ];

      // Build search promise with parameters
      const results = await getPosterService('values').search(
        this.defaultTemplate.templateId,
        this.selectedSearchMode.search.searchSourceId,
        parameters,
        pagination,
        sorts
      );

      return results;
    },
    /**
     * Add selected search results to signages list to import.
     */
    addToImportCart() {
      if (
        !this.selectedSearchResults ||
        !this.selectedSearchResults.length ||
        !this.importFamily ||
        !this.defaultTypeData ||
        !this.importConfiguration.summary
      ) {
        return;
      }

      const newSignageItems = [];

      // Create signage item with selected search results
      for (const selectedSearchResult of this.selectedSearchResults) {
        // Create new signage item
        const newSignageItem = getPosterService('signages').buildnewSignageItem(
          getPosterService('signages').getNewSignageId(),
          this.importFamily ? this.importFamily.itemId : null,
          this.user.extendedProperties
        );

        if (this.importType) {
          // Copy the type to values for the table
          newSignageItem.values.type = this.importType;

          // The type data should have been loaded before we get to this point
          const importTypeData = getPosterService('templates').getTypeData(
            this.importType.itemId,
            PosterScopes.BatchCreation
          );
          if (importTypeData) {
            const defaultFormat = getPosterService('templates').getDefaultTemplateTypeFormat(
              importTypeData.formatGroups,
              null
            );
            newSignageItem.formatId = defaultFormat ? defaultFormat.itemId : null;

            // Setup default values, formatsCopies
            getPosterService('signages').updateSignageItemForType(
              newSignageItem,
              importTypeData,
              (signageItem, alias, value, attribute) => {
                getPosterService('signages').setPosterTaintedValue(signageItem, alias, false);
                getPosterService('signages').setPosterValue(
                  signageItem,
                  alias,
                  value,
                  attribute,
                  importTypeData,
                  'replace'
                );
              }
            );
          }
        }

        // Add values of selected search result
        for (const summaryColumn of this.importConfiguration.summary.columns) {
          if (
            selectedSearchResult.hasOwnProperty(summaryColumn.value) &&
            selectedSearchResult[summaryColumn.value] != null
          ) {
            newSignageItem.values[summaryColumn.value] = selectedSearchResult[summaryColumn.value];
          }
        }

        // Copy the formatsCopiesMap and id to values for the table
        // formatsCopiesMap is "exposed" to be used by a table column
        newSignageItem.values.formatsCopiesMap = newSignageItem.formatsCopiesMap;
        // The id is to identify a poster in the array and is not exposed
        newSignageItem.values._id = newSignageItem.id;

        newSignageItems.push(newSignageItem);
      }

      // Select items values in summary table BEFORE they are added to the table,
      // to avoid a view update from non-selected to selected
      this.$refs.importCartTable.selectItems(newSignageItems.map(item => item.values)); // Batch select all new items
      this.signagesToImport.push(...newSignageItems); // Add items to cart
      this.sortCart();
      if (this.$refs.importCartTable != null) {
        // Update our selection data to match the table's
        this.importValuesSelection = this.$refs.importCartTable.getSelectedItems();
      }
    },
    /**
     * Delete selected signage item from import selection.
     */
    deleteFromImportSelection() {
      if (this.$refs.importCartTable != null) {
        for (const signageValues of this.importValuesSelection) {
          const indexToDelete = this.signagesToImport.findIndex(
            signage => signage.values === signageValues
          );
          this.signagesToImport.splice(indexToDelete, 1);
          this.$refs.importCartTable.resetSelection();
          this.importValuesSelection = this.$refs.importCartTable.getSelectedItems();
        }
      }
    },
    /**
     * Reloads the page to recover from a type data (form, formats) or fonts error
     */
    reloadPage() {
      this.$router.go();
    },
    /**
     * Import signage selection
     */
    async importSignages() {
      const stopLoading = this.loader.addMessage();
      try {
        // Add new imported signages
        const getValuesPromises = [];
        const tempPosterCart = [];
        for (const importPoster of this.signagesToImport) {
          const newSignageItem = JSON.parse(JSON.stringify(importPoster));

          // Set the type and formatsCopiesMap to be what was confirmed in the import cart
          newSignageItem.type = newSignageItem.values.type;
          newSignageItem.formatsCopiesMap = newSignageItem.values.formatsCopiesMap;
          // Delete the old values to avoid confusion
          delete newSignageItem.values.type;
          delete newSignageItem.values.formatsCopiesMap;
          delete newSignageItem.values._id;

          getPosterService('signages').selectFirstFormatWithCopies(newSignageItem);

          const typeData = await this.getTypeData(newSignageItem.type);
          // Init default values, formats... (should not change formatId, formatCopiesMap if valid)
          getPosterService('signages').updateSignageItemForType(
            newSignageItem,
            typeData,
            (poster, alias, value, attribute) => {
              getPosterService('signages').setPosterTaintedValue(poster, alias, false);
              getPosterService('signages').setPosterValue(
                poster,
                alias,
                value,
                attribute,
                typeData,
                'replace'
              );
            }
          );

          // Add it to our temp cart
          tempPosterCart.push(newSignageItem);

          // Step 1 : Add first getValues call for each signage items
          getValuesPromises.push(
            new Promise(resolve => {
              // Create IIFE so we can use await safely
              (async () => {
                const typeData = await this.getTypeData(newSignageItem.type);

                if (!typeData || !typeData.form || !typeData.form.inputGroups) {
                  // No type data
                  return resolve({ newSignageItem, newType: null });
                }

                const signageItemTemplate = typeData.templates.get(newSignageItem.formatId);

                try {
                  const bindings = await getPosterService('values')
                    .loadBindings(
                      signageItemTemplate != null ? signageItemTemplate.templateId : null,
                      typeData,
                      newSignageItem,
                      false
                    )
                    .catch(() => {
                      // Ignore error so we can still init formats, summary...
                      return null;
                    });

                  if (bindings) {
                    // Update values / tainted and inputs summary
                    getPosterService('signages').mergePosterValues(
                      newSignageItem,
                      bindings.values,
                      typeData
                    );
                    getPosterService('signages').mergePosterTaintedValues(
                      newSignageItem,
                      bindings.taintedValues
                    );

                    // If the current type is not the same as the new type,
                    // return it as 'newType' to auto change it in the 2nd step
                    if (newSignageItem.type.alias !== bindings.type) {
                      const familyRootTypes = await getPosterService(
                        'templates'
                      ).loadFamilyRootTypes(newSignageItem.familyId);
                      const newType = getPosterService('templates').getTemplateTypeByAlias(
                        familyRootTypes,
                        bindings.type
                      );

                      return resolve({ newSignageItem, newType });
                    } else {
                      // If the type didn't change, use the current type to apply the formats
                      newSignageItem.formatsCopiesMap = getPosterService(
                        'signages'
                      ).formatInfoToCopiesMap(typeData.formatGroups, bindings.formats);
                    }
                  }

                  // Otherwise no type change, or failed to get bindings

                  // Initialize (clean) formats copies
                  getPosterService('signages').initFormatsCopies(
                    newSignageItem,
                    typeData.formatGroups
                  );
                  // Set default format
                  const defaultFormat = getPosterService('templates').getDefaultTemplateTypeFormat(
                    typeData.formatGroups,
                    newSignageItem.formatId
                  );
                  newSignageItem.formatId = defaultFormat ? defaultFormat.itemId : null;

                  getPosterService('signages').updateInputsSummary(newSignageItem);
                } catch (err) {
                  // Ignore error
                }
                return resolve({ newSignageItem, newType: null });
              })();
            })
          );
        }

        // Step 2 : Wait for all getValues calls, then get all typeData for each new types
        const successValuesData = await Promise.all(getValuesPromises);
        const newTypes = [];
        const typeDataPromises = [];

        for (const valuesData of successValuesData) {
          const newType = valuesData.newType;
          if (!newTypes.includes(newType) && newType != null) {
            newTypes.push(newType);
            // Add get type data promise if new type is not null and not already pushed
            typeDataPromises.push(
              this.getTypeData(newType).then(newTypeData => ({ newTypeData, newType }))
            );
          }
        }

        // Step 3 : Wait for all new getTypeData calls,
        // then add second getValues call for each signage items
        const successTypeData = await Promise.all(typeDataPromises);
        const valuesPromises = [];

        for (const valuesData of successValuesData) {
          const newType = valuesData.newType;
          const newSignageItem = valuesData.newSignageItem;

          // If the poster didn't have a new type, skip this step
          if (newType == null) {
            continue;
          }

          // Initialize signage items with new type
          const newTypeData = successTypeData.find(typeData => typeData.newType === newType)
            .newTypeData;
          getPosterService('signages').updateSignageItemForType(
            newSignageItem,
            newTypeData,
            (poster, alias, value, attribute) => {
              getPosterService('signages').setPosterTaintedValue(poster, alias, false);
              getPosterService('signages').setPosterValue(
                poster,
                alias,
                value,
                attribute,
                newTypeData,
                'replace'
              );
            }
          );
          const signageItemTemplate = newTypeData.templates.get(newSignageItem.formatId);
          // Push getValues promise for the signage item
          valuesPromises.push(
            new Promise(resolve => {
              // Create IIFE so we can use await safely
              (async () => {
                try {
                  const bindings = await getPosterService('values').loadBindings(
                    signageItemTemplate != null ? signageItemTemplate.templateId : null,
                    newTypeData,
                    newSignageItem,
                    false
                  );

                  // Update values / tainted and inputs summary
                  getPosterService('signages').mergePosterValues(
                    newSignageItem,
                    bindings.values,
                    newTypeData
                  );
                  getPosterService('signages').mergePosterTaintedValues(
                    newSignageItem,
                    bindings.taintedValues
                  );

                  // Apply new formatsCopiesMap with the new type data
                  newSignageItem.formatsCopiesMap = getPosterService(
                    'signages'
                  ).formatInfoToCopiesMap(newTypeData.formatGroups, bindings.formats);
                } catch (err) {
                  // Do nothing
                }

                // Initialize (clean) formats copies
                getPosterService('signages').initFormatsCopies(
                  newSignageItem,
                  newTypeData.formatGroups
                );
                // Set default format
                const defaultFormat = getPosterService('templates').getDefaultTemplateTypeFormat(
                  newTypeData.formatGroups,
                  newSignageItem.formatId
                );
                newSignageItem.formatId = defaultFormat ? defaultFormat.itemId : null;

                getPosterService('signages').updateInputsSummary(newSignageItem);

                resolve();
              })();
            })
          );
        }

        // Execute all getValues promises created in step 3
        await Promise.all(valuesPromises);

        const firstPoster = tempPosterCart[0];
        this.posterCart.unshift(...tempPosterCart);
        // Select the first poster of the newly imported posters
        store.commit(`${POSTER_NAMESPACE}/${SET_CURRENT_SIGNAGE_ITEM_ID}`, firstPoster.id);
        services.getService('analytics').trackEvent(this.Tracking.QUICK_IMPORT_SIGNAGES, null, {
          signagesNumber: tempPosterCart.length,
        });

        const firstPosterFamily = await getPosterService('templates').loadFamily(
          firstPoster.familyId
        );
        store.commit(`${POSTER_NAMESPACE}/${SET_LAST_BUILT_FAMILY}`, firstPosterFamily);
        const firstPosterType = await getPosterService('templates').loadType(
          firstPoster.type.itemId
        );
        store.commit(`${POSTER_NAMESPACE}/${SET_LAST_BUILT_TYPE}`, firstPosterType);

        this.$router.push({ name: 'posterCreation' });
      } catch (err) {
        services.getService('alerts').alertError(this.$t('poster.creation.error.import_signages'));
      }

      stopLoading();
    },
  },
  mounted() {
    this.loadInitialData();
  },
};
</script>
